<script setup lang="ts">
import { computed, ref } from 'vue'
import dictionariesHttpService from '@/services/http/dictionariesHttpService.ts'
import { useRoute } from 'vue-router'
import type { Id } from '@raildev/shared-utils'
import { useToast } from 'primevue/usetoast'
import type { Dictionary } from '@/services/http/httpResponses.ts'
import { Roles } from '@/constants.ts'
import jwtService from '@/services/jwtService.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin],
  },
})

const route = useRoute()

const dictionaryType = ref<string | null>(route.query.type as string | null)

const supportedLanguagesResponse = await dictionariesHttpService.getSupportedLanguages()
const languages = ref(supportedLanguagesResponse.languages)

const values = ref<Dictionary[]>([])
const toast = useToast()

const categoriesDictionaryValuesResponse = await dictionariesHttpService.getCategoryDictionary()

const categories = ref(categoriesDictionaryValuesResponse.values)

const getDictionaries = async () => {
  if (dictionaryType.value == null) {
    return []
  }

  const dictionaryValuesResponse = await dictionariesHttpService.getDictionaryValues(
    dictionaryType.value,
  )
  return dictionaryValuesResponse.values
}

values.value = await getDictionaries()

const hasCategory = computed(() => {
  const firstValue = values.value[0]

  if (firstValue != null) {
    return firstValue.categoryId != null
  }
  return false
})

const escapeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''

  let str = String(value)

  if (/[",\n]/.test(str)) {
    str = str.replace(/"/g, '""')
    str = `"${str}"`
  }

  return str
}

const downloadCsvTemplate = () => {
  const idField = 'id'
  const categoryIdField = 'categoryId'
  const languageCodes = languages.value.map((x) => escapeCsvValue(x.code))

  const headers = [idField, ...(hasCategory.value ? [categoryIdField] : []), ...languageCodes]

  const rows = values.value.map((x) => {
    const csvRow: Record<string, string> = {}
    csvRow[idField] = x.id

    if (hasCategory.value) {
      csvRow[categoryIdField] = x.categoryId!
    }

    for (const languageCode of languageCodes) {
      const translationValue = x.translations.find((t) => t.language === languageCode)?.value
      csvRow[escapeCsvValue(languageCode)] = escapeCsvValue(translationValue)
    }
    return csvRow
  })

  const csvBody = rows.map((row) => {
    const csvLine = []

    for (const header of headers) {
      const value = row[header] || ''
      csvLine.push(value)
    }
    return csvLine.join(',')
  })

  const csvContent = headers.join(',') + '\n' + csvBody.join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${dictionaryType.value}.csv`)
  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const uploadCsvTemplate = async () => {
  toast.add({
    severity: 'success',
    detail: 'Завершено',
    life: 1000,
  })

  values.value = await getDictionaries()
}
const beforeUploadCsvTemplate = ({ xhr }: { xhr: XMLHttpRequest }) => {
  const token = jwtService.getToken()
  xhr.setRequestHeader('Authorization', `Bearer ${token}`)
  toast.add({
    severity: 'info',
    detail: 'Загружается',
    life: 1000,
  })
}

const getCategoryName = (categoryId: Id) => {
  return (
    categories.value
      .find((x) => x.id === categoryId)
      ?.translations.find((x) => x.language == 'ru-RU')?.value ?? '-'
  )
}
</script>

<template>
  <div class="flex flex-col gap-4 w-full">
    <DataTable ref="dt" :value="values" dataKey="id" tableClass="w-full">
      <template #header>
        <div class="flex flex-wrap gap-2 items-center justify-between">
          <h4 class="m-0 font-bold">{{ dictionaryType }}</h4>
          <div class="flex flex-wrap gap-4">
            <Button label="Скачать данные" icon="pi pi-download" @click="downloadCsvTemplate" />
            <FileUpload
              mode="basic"
              name="csvFile"
              :url="`/api/v1/admin/dictionaries/${dictionaryType}`"
              accept=".csv"
              auto
              choose-label="Загрузить CSV файл"
              upload-icon="pi pi-upload"
              @beforeSend="beforeUploadCsvTemplate"
              @upload="uploadCsvTemplate"
              :maxFileSize="1000000"
            />
          </div>
        </div>
      </template>
      <Column header="Id" field="id" style="min-width: 12rem"> </Column>
      <Column v-if="hasCategory" header="Id категории" style="min-width: 12rem">
        <template #body="slotProps">
          {{ (slotProps.data as Dictionary).categoryId }}
        </template>
      </Column>
      <Column v-if="hasCategory" header="Категория" style="min-width: 12rem">
        <template #body="slotProps">
          {{ getCategoryName((slotProps.data as Dictionary).categoryId!) }}
        </template>
      </Column>
      <Column
        v-for="lang in languages"
        :key="lang.code"
        :header="`${lang.name} (${lang.code})`"
        style="min-width: 12rem"
      >
        <template #body="slotProps">
          {{
            (slotProps.data as Dictionary).translations.find((x) => x.language === lang.code)?.value
          }}
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col items-center gap-2 py-6 text-gray-400">
          <i class="pi pi-inbox text-4xl" />
          <span>Данные отсутствуют</span>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<style scoped></style>
